import React, { FC, useEffect, useState, lazy, Suspense } from "react";
import { Container, Nav, Navbar } from "react-bootstrap";
import { Link, NavLink, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { toAbsoluteUrl } from "../helpers";

const languages = [
  { value: "en", label: "EN" },
  { value: "de", label: "DE" },
  { value: "id", label: "ID" },
];

interface OptionType {
  label: string;
  value: string;
}

type Props = {
  products?: any;
};

const atras_ip: any = process.env.REACT_APP_ATRAS_IP;
const LazyCartButton = lazy(() => import("../components/buttons/CartButton"));
const LazySearchPopup = lazy(() => import("../components/search/SearchPopup"));

const Header: FC<Props> = ({ products }) => {
  const location = useLocation();
  const currentRoute = location.pathname;
  const currentLanguageCode = Cookies.get("i18next") || "en";
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isNavbarExpanded, setIsNavbarExpanded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const initialSelectedOption = languages.find(
    (language) => language.value === currentLanguageCode
  );

  const [selectedOption] = useState<OptionType | null>(
    initialSelectedOption || null
  );

  const toggleNavbar = () => {
    setIsNavbarExpanded(!isNavbarExpanded);
  };
  const toggleDropdown = () => {
    setShowDropdown((prevState) => !prevState);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    // Add event listener for scroll
    window.addEventListener("scroll", handleScroll);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <Navbar
        expand="xl"
        className={isScrolled || isNavbarExpanded ? "scrolled-navbar" : ""}
        fixed={"top"}
      >
        <Container>
          <Navbar.Brand>
            <Link className={"mt-2"} to="/">
              <img
                src={toAbsoluteUrl("/assets/images/logos/logo.png")}
                alt="Garden of Life"
                className="logo"
              />
            </Link>
          </Navbar.Brand>
          <>
            <div className={"d-flex"}>
              <Link
                to={atras_ip}
                className={`d-xl-none d-block btn btn-main mt-1 f-text-regular fs-13`}
              >
                Login
              </Link>

              <Suspense fallback={<></>}>
                <LazyCartButton
                  currentRoute={currentRoute}
                  className={"d-xl-none d-block"}
                  products={products}
                />
              </Suspense>

              <Navbar.Toggle
                onClick={toggleNavbar}
                aria-controls="navbarScroll"
              >
                <img
                  src={toAbsoluteUrl("/assets/images/nav/list-icon.svg")}
                  alt={"Garden Of Life Icon"}
                />
              </Navbar.Toggle>
            </div>

            <Navbar.Collapse
              className={"container justify-content-end"}
              id="navbarScroll"
            >
              <Nav
                className="text-lg-start align-items-center p-lg-0 p-3"
                navbarScroll
              >
                <NavLink
                  to="/projects"
                  className={`nav-link ${
                    location.pathname.includes("/project") ? "active" : ""
                  }`}
                >
                  {t("header.projects")}
                </NavLink>
                <NavLink
                  className={`nav-link ${
                    location.pathname.includes("/metaverse") ? "active" : ""
                  }`}
                  to="/metaverse"
                >
                  Metaverse
                </NavLink>
                <NavLink
                  to="/shop"
                  className={`nav-link ${
                    location.pathname.includes("/shop") && "active"
                  }`}
                >
                  Shop
                </NavLink>
                <NavLink
                  to="/charity"
                  className={`nav-link ${
                    location.pathname.includes("/charity") && "active"
                  }`}
                >
                  Charity
                </NavLink>
                <NavLink
                  to="/blogs"
                  className={`nav-link ${
                    location.pathname.includes("/blog") && "active"
                  }`}
                >
                  {t("header.blog")}
                </NavLink>
                <NavLink
                  className={`nav-link ${
                    location.pathname.includes("/faqs") && "active"
                  }`}
                  to="/faqs"
                >
                  {t("header.faqs")}
                </NavLink>
                <NavLink
                  to="/about"
                  className={`nav-link ${
                    location.pathname.includes("/terms-and-conditions") ||
                    location.pathname.includes("/privacy-policy") ||
                    location.pathname.includes("/cookie-policy") ||
                    location.pathname.includes("/support") ||
                    location.pathname.includes("/about")
                      ? "active"
                      : ""
                  }`}
                >
                  {t("header.about")}
                </NavLink>
                <NavLink
                  to="/contact"
                  className={`nav-link ${
                    location.pathname.includes("/contact") && "active"
                  }`}
                >
                  {t("header.contact")}
                </NavLink>

                <div className={"nav-link cursor-pointer"}>
                  <i
                    className={"fa-solid fa-magnifying-glass"}
                    onClick={() => setShowSearch(!showSearch)}
                  ></i>
                </div>

                <Link
                  to={atras_ip}
                  className={`d-xl-block d-none btn btn-main f-text-regular fs-13`}
                  target="_blank"
                >
                  Login
                </Link>

                <div className={`ms-2 nav-item dropdown`}>
                  <button
                    className="btn btn-select dropdown-toggle dropdown-toggle"
                    type="button"
                    id="dropdownMenuButton1"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    onClick={toggleDropdown}
                  >
                    <span>
                      {" "}
                      <span className={"pt-1 fs-13"}>
                        {selectedOption?.label}
                      </span>{" "}
                    </span>
                  </button>
                  {showDropdown && (
                    <ul
                      className="dropdown-menu select-lang d-block"
                      aria-labelledby="dropdownMenuButton1"
                    >
                      {languages.map(({ value, label }) => (
                        <li key={value} className={"text-center"}>
                          <a
                            href={`/${value}${currentRoute.replace(
                              /^\/(de|en|id)\//,
                              "/"
                            )}`}
                            className={"dropdown-item fs-14 text-center"}
                          >
                            {label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <Suspense fallback={<></>}>
                  <LazyCartButton
                    currentRoute={currentRoute}
                    className={"d-xl-block d-none"}
                    products={products}
                  />
                </Suspense>
              </Nav>
            </Navbar.Collapse>
          </>
        </Container>
      </Navbar>

      {showSearch && (
        <Suspense fallback={<></>}>
          <LazySearchPopup showPop={showSearch} setShowPop={setShowSearch} />
        </Suspense>
      )}
    </>
  );
};

export default Header;
